// src/pages/InvoicesLedger.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet,
  Search,
  Calendar,
  User,
  AlertTriangle,
  RefreshCw,
  Eye,
  Lock,
  Upload,
  TrendingUp,
  Download,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
} from 'lucide-react';
import { apiRequest } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { SalesInvoice, SalesInvoiceItem } from '../types';
import { Pagination } from '../components/ui/Pagination';
import { InvoiceDetailModal } from '../components/InvoiceDetailModal';

export const InvoicesLedger: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const canViewCost = user?.permissions.includes('pricing.view_cost') ?? false;

  // URL query params
  const initialPeriod = searchParams.get('period') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialRep = searchParams.get('salesPerson') || '';

  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [salesPersonFilter, setSalesPersonFilter] = useState(initialRep);
  const [periodFilter, setPeriodFilter] = useState(initialPeriod);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [marginFilter, setMarginFilter] = useState('');
  const [negativeStockOnly, setNegativeStockOnly] = useState(false);

  // Pagination State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalInvoices, setTotalInvoices] = useState(0);

  // Data State
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  // Metric Summary Strip State
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalCount: 0,
    totalBilled: 0,
    totalBaseCost: 0,
    totalGp: 0,
    paidCount: 0,
    unpaidCount: 0,
    overdueCount: 0,
    negativeStockCount: 0,
    salesReps: [] as string[],
  });

  // Invoice Detail Modal State
  const [selectedInvoice, setSelectedInvoice] = useState<SalesInvoice | null>(null);
  const [selectedItems, setSelectedItems] = useState<SalesInvoiceItem[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const formatAED = (val?: number) => {
    if (val === undefined || val === null) return '—';
    return `AED ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const renderStatusBadge = (status: string) => {
    const s = status.toLowerCase();
    let bg = 'bg-slate-100 text-slate-800 border-slate-200';
    if (s === 'paid') bg = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s === 'unpaid') bg = 'bg-amber-50 text-amber-700 border-amber-200';
    if (s === 'overdue') bg = 'bg-rose-50 text-rose-700 border-rose-200';
    if (s === 'cancelled') bg = 'bg-slate-100 text-slate-500 border-slate-300 line-through';
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${bg}`}>
        {status}
      </span>
    );
  };

  const renderGpBadge = (margin?: number) => {
    if (margin === undefined || margin === null) return null;
    let color = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (margin < 0) color = 'bg-rose-50 text-rose-700 border-rose-200';
    else if (margin < 20) color = 'bg-amber-50 text-amber-700 border-amber-200';
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold border ${color}`}>
        {margin > 0 ? '+' : ''}{margin.toFixed(1)}%
      </span>
    );
  };

  // Open invoice line items modal
  const openInvoiceDetails = async (inv: SalesInvoice) => {
    setSelectedInvoice(inv);
    setLoadingDetail(true);
    try {
      const res = await apiRequest<{ invoice: SalesInvoice; items: SalesInvoiceItem[] }>(`/api/invoices/${inv.id}`);
      if (res) {
        setSelectedItems(res.items || []);
      }
    } catch {
      showToast('Failed to load invoice items', 'error');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Load Invoices
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (searchQuery) params.set('search', searchQuery);
      if (salesPersonFilter) params.set('salesPerson', salesPersonFilter);
      if (periodFilter) params.set('period', periodFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (marginFilter) params.set('margin', marginFilter);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (negativeStockOnly) params.set('negativeStock', 'true');

      const data = await apiRequest<{ items: SalesInvoice[]; pagination: { total: number } }>(
        `/api/invoices?${params.toString()}`
      );

      if (data) {
        setInvoices(data.items || []);
        setTotalInvoices(data.pagination?.total || 0);
      }
    } catch {
      showToast('Failed to load invoices ledger', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, salesPersonFilter, periodFilter, startDate, endDate, marginFilter, statusFilter, negativeStockOnly, showToast]);

  // Load high-level overview metrics for this ledger
  const loadOverviewMetrics = useCallback(async () => {
    try {
      const data = await apiRequest<any>('/api/invoices/analytics/dashboard?includeCancelled=true');
      if (data) {
        const reps = (data.sales_reps || []).map((r: any) => r.sales_person).filter(Boolean);
        const unpaid = data.unpaid_invoices_count ?? 0;
        const total = data.total_invoices ?? 0;
        setSummaryMetrics({
          totalCount: total,
          totalBilled: data.total_sold_amount || 0,
          totalBaseCost: data.total_base_cost || 0,
          totalGp: data.total_gp_amount || 0,
          paidCount: Math.max(0, total - unpaid),
          unpaidCount: unpaid,
          overdueCount: data.overdue_invoices_count ?? 0,
          negativeStockCount: data.negative_stock_invoices_count ?? 0,
          salesReps: reps,
        });
      }
    } catch {
      // Metrics are supplementary
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadOverviewMetrics();
  }, [loadOverviewMetrics]);

  // Reset page when filters change
  const handleFilterChange = (setter: (v: any) => void, val: any) => {
    setter(val);
    setPage(1);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSalesPersonFilter('');
    setPeriodFilter('');
    setStartDate('');
    setEndDate('');
    setMarginFilter('');
    setNegativeStockOnly(false);
    setPage(1);
    setSearchParams({});
  };

  const totalPages = Math.ceil(totalInvoices / pageSize);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="p-2 bg-emerald-50 text-emerald-700 rounded-xl border border-emerald-200">
              <FileSpreadsheet className="w-6 h-6" />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Sales Invoices Ledger</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Comprehensive directory of sales invoices captured from Tally, ERPNext & inbound webhook stream.
              </p>
            </div>
          </div>
        </div>

        {/* Global Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/sales-profitability')}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold border border-slate-200 transition"
          >
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            GP & Profitability Tracker
          </button>
          <button
            type="button"
            onClick={() => navigate('/sales-profitability?tab=import')}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-semibold shadow-sm transition"
          >
            <Upload className="w-4 h-4" />
            Import Invoices (Tally / ERP)
          </button>
        </div>
      </div>

      {/* KPI Metric Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Total Invoices */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block">Total Invoices</span>
          <span className="text-xl font-bold text-slate-900 mt-1 block">{summaryMetrics.totalCount}</span>
          <span className="text-[11px] text-slate-400">Captured in database</span>
        </div>

        {/* Total Billed */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block">Total Billed</span>
          <span className="text-lg font-bold text-slate-900 font-mono mt-1 block truncate">
            {formatAED(summaryMetrics.totalBilled)}
          </span>
          <span className="text-[11px] text-slate-400">Gross billing amount</span>
        </div>

        {/* Gross Profit (if authorized) */}
        {canViewCost ? (
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 block">Total GP</span>
            <span className="text-lg font-bold text-emerald-700 font-mono mt-1 block truncate">
              {formatAED(summaryMetrics.totalGp)}
            </span>
            <span className="text-[11px] text-emerald-600">Net commercial spread</span>
          </div>
        ) : (
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block">Currency</span>
            <span className="text-xl font-bold text-slate-900 mt-1 block">AED</span>
            <span className="text-[11px] text-slate-400">Standard operational currency</span>
          </div>
        )}

        {/* Paid Status */}
        <div
          onClick={() => handleFilterChange(setStatusFilter, statusFilter === 'paid' ? 'all' : 'paid')}
          className={`p-4 rounded-xl border cursor-pointer transition ${
            statusFilter === 'paid'
              ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-500'
              : 'bg-white border-slate-200 hover:border-emerald-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Paid Invoices</span>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          </div>
          <span className="text-xl font-bold text-emerald-700 mt-1 block">{summaryMetrics.paidCount}</span>
          <span className="text-[11px] text-slate-400">Settled accounts</span>
        </div>

        {/* Unpaid / Pending */}
        <div
          onClick={() => handleFilterChange(setStatusFilter, statusFilter === 'unpaid' ? 'all' : 'unpaid')}
          className={`p-4 rounded-xl border cursor-pointer transition ${
            statusFilter === 'unpaid'
              ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-500'
              : 'bg-white border-slate-200 hover:border-amber-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Unpaid</span>
            <Clock className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <span className="text-xl font-bold text-amber-700 mt-1 block">{summaryMetrics.unpaidCount}</span>
          <span className="text-[11px] text-slate-400">Awaiting payment</span>
        </div>

        {/* Negative Stock Invoices Alert */}
        <div
          onClick={() => handleFilterChange(setNegativeStockOnly, !negativeStockOnly)}
          className={`p-4 rounded-xl border cursor-pointer transition ${
            negativeStockOnly
              ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-500'
              : 'bg-white border-slate-200 hover:border-rose-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Negative Stock</span>
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
          </div>
          <span className="text-xl font-bold text-rose-700 mt-1 block">
            {summaryMetrics.negativeStockCount}
          </span>
          <span className="text-[11px] text-rose-600 font-medium">Billed with deficit</span>
        </div>
      </div>

      {/* Main Filter Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
        {/* Row 1: Search & Status Pills */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          {/* Quick Search */}
          <div className="relative flex-1 min-w-[280px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search by Invoice # (e.g. INV-2026-0001), Customer, or Sales Rep..."
              value={searchQuery}
              onChange={(e) => handleFilterChange(setSearchQuery, e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => handleFilterChange(setSearchQuery, '')}
                className="absolute right-3 top-2.5 text-xs text-slate-400 hover:text-slate-600"
              >
                Clear
              </button>
            )}
          </div>

          {/* Quick Status Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 lg:pb-0">
            {[
              { id: 'all', label: 'All Invoices' },
              { id: 'active', label: 'Active Only' },
              { id: 'paid', label: 'Paid' },
              { id: 'unpaid', label: 'Unpaid' },
              { id: 'overdue', label: 'Overdue' },
              { id: 'cancelled', label: 'Cancelled' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleFilterChange(setStatusFilter, tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition whitespace-nowrap ${
                  statusFilter === tab.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: Secondary Dropdown Filters */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 text-xs">
          {/* Sales Representative */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
            <User className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={salesPersonFilter}
              onChange={(e) => handleFilterChange(setSalesPersonFilter, e.target.value)}
              className="bg-transparent text-slate-700 font-medium focus:outline-none text-xs"
            >
              <option value="">All Sales Reps</option>
              {summaryMetrics.salesReps.map((rep) => (
                <option key={rep} value={rep}>{rep}</option>
              ))}
            </select>
          </div>

          {/* Period Filter (YYYY-MM) */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Period (e.g. 2026-08)"
              value={periodFilter}
              onChange={(e) => handleFilterChange(setPeriodFilter, e.target.value)}
              className="bg-transparent text-slate-700 font-mono text-xs w-28 focus:outline-none"
            />
          </div>

          {/* Date Range Start */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
            <span className="text-slate-400">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleFilterChange(setStartDate, e.target.value)}
              className="bg-transparent text-slate-700 text-xs focus:outline-none"
            />
          </div>

          {/* Date Range End */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
            <span className="text-slate-400">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleFilterChange(setEndDate, e.target.value)}
              className="bg-transparent text-slate-700 text-xs focus:outline-none"
            />
          </div>

          {/* Margin Filter (if authorized) */}
          {canViewCost && (
            <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={marginFilter}
                onChange={(e) => handleFilterChange(setMarginFilter, e.target.value)}
                className="bg-transparent text-slate-700 font-medium focus:outline-none text-xs"
              >
                <option value="">All Margins</option>
                <option value="healthy">Healthy Margin (≥ 20%)</option>
                <option value="low">Low Margin (&lt; 20%)</option>
                <option value="negative">Negative Margin (&lt; 0%)</option>
              </select>
            </div>
          )}

          {/* Negative Stock Toggle Filter */}
          <button
            type="button"
            onClick={() => handleFilterChange(setNegativeStockOnly, !negativeStockOnly)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-medium transition ${
              negativeStockOnly
                ? 'bg-rose-100 text-rose-800 border-rose-300 font-bold'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            Negative Stock Only
          </button>

          {/* Page size selector */}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-slate-400">Rows:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 text-xs"
            >
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            {(searchQuery || statusFilter !== 'all' || salesPersonFilter || periodFilter || startDate || endDate || marginFilter || negativeStockOnly) && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs text-rose-600 hover:text-rose-800 underline font-medium ml-2"
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Invoices Table Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Table Header Info */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
          <div className="text-xs text-slate-600">
            Showing <strong className="text-slate-900">{invoices.length}</strong> of{' '}
            <strong className="text-slate-900">{totalInvoices}</strong> invoices
            {periodFilter && (
              <span className="ml-2 font-mono bg-slate-200 text-slate-800 px-2 py-0.5 rounded text-[11px]">
                Period: {periodFilter}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadInvoices()}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded transition"
              title="Refresh ledger"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase bg-slate-50">
                <th className="py-3 px-4">Invoice #</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Customer Name</th>
                <th className="py-3 px-4">Sales Rep</th>
                <th className="py-3 px-4 text-right">Total Billed</th>
                {canViewCost && <th className="py-3 px-4 text-right">Base Cost</th>}
                {canViewCost && <th className="py-3 px-4 text-right">Gross Profit</th>}
                {canViewCost && <th className="py-3 px-4 text-right">GP Margin</th>}
                <th className="py-3 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={canViewCost ? 10 : 7} className="text-center py-12 text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto text-emerald-600 mb-2" />
                    <span>Loading ERP invoices...</span>
                  </td>
                </tr>
              ) : invoices.length > 0 ? (
                invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    onClick={() => openInvoiceDetails(inv)}
                    className={`hover:bg-slate-50/80 cursor-pointer transition ${
                      inv.status === 'cancelled' ? 'bg-rose-50/20' : ''
                    }`}
                  >
                    {/* Invoice # with badges */}
                    <td className="py-3.5 px-4 font-mono font-bold text-emerald-800">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="hover:underline">{inv.invoice_no}</span>
                        {inv.is_locked && (
                          <span
                            title={`Period ${inv.period || ''} is locked`}
                            className="inline-flex items-center text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded text-[10px] font-sans font-medium"
                          >
                            <Lock className="w-2.5 h-2.5 mr-0.5" />
                            Locked
                          </span>
                        )}
                        {Boolean(inv.has_active_negative_stock) && (
                          <span
                            title="Invoice generated with negative physical stock. Production batch required to clear."
                            className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full text-[10px] font-sans font-bold whitespace-nowrap animate-pulse"
                          >
                            <AlertTriangle className="w-2.5 h-2.5 text-rose-600" />
                            Negative Stock
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Date */}
                    <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap font-mono text-xs">
                      {inv.invoice_date}
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      {renderStatusBadge(inv.status)}
                    </td>

                    {/* Customer */}
                    <td className="py-3.5 px-4 font-medium text-slate-800 max-w-[220px] truncate" title={inv.customer_name}>
                      {inv.customer_name}
                    </td>

                    {/* Sales Person */}
                    <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap">
                      {inv.sales_person}
                    </td>

                    {/* Total Billed */}
                    <td
                      className={`py-3.5 px-4 text-right font-bold font-mono ${
                        inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-900'
                      }`}
                    >
                      {formatAED(inv.total_sold_amount)}
                    </td>

                    {/* Base Cost */}
                    {canViewCost && (
                      <td
                        className={`py-3.5 px-4 text-right font-mono text-xs ${
                          inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-600'
                        }`}
                      >
                        {formatAED(inv.total_base_cost)}
                      </td>
                    )}

                    {/* GP Amount */}
                    {canViewCost && (
                      <td
                        className={`py-3.5 px-4 text-right font-semibold font-mono ${
                          inv.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-emerald-700'
                        }`}
                      >
                        {formatAED(inv.total_gp_amount)}
                      </td>
                    )}

                    {/* GP % */}
                    {canViewCost && (
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        {inv.status === 'cancelled' ? (
                          <span className="text-xs text-rose-500 font-medium">Voided</span>
                        ) : (
                          renderGpBadge(inv.gp_margin_pct)
                        )}
                      </td>
                    )}

                    {/* Action button */}
                    <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => openInvoiceDetails(inv)}
                        className="p-1.5 hover:bg-slate-200 rounded text-slate-600 hover:text-slate-900 transition"
                        title="View invoice line items & GP breakdown"
                      >
                        <Eye className="w-4 h-4 text-emerald-700" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canViewCost ? 10 : 7} className="text-center py-12 text-slate-400">
                    <p className="text-sm">No invoices found matching your current filters.</p>
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="mt-2 text-xs text-emerald-700 hover:underline font-semibold"
                    >
                      Clear all filters
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Built-in Pagination Footer */}
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalInvoices}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      {/* Line Item Breakdown Drill-Down Modal */}
      <InvoiceDetailModal
        invoice={selectedInvoice}
        items={selectedItems}
        loading={loadingDetail}
        canViewCost={canViewCost}
        onClose={() => setSelectedInvoice(null)}
      />
    </div>
  );
};

export default InvoicesLedger;
